import { lockScroll, unlockScroll, updateAllHeaderCustomProperties } from '@theme/utilities';

/** One link tree; native dialog supplies modality, inert background and focus containment. */
class GWCategoryNavigation extends HTMLElement {
  #abort;
  #observer;
  #visibilityObserver;
  #media = window.matchMedia('(min-width: 990px)');
  #visits = new Map();
  #selected = null;
  #railScroll = 0;
  #opening = false;
  #openRequest = 0;
  #backdropDown = false;

  connectedCallback() {
    if (this.hasAttribute('data-ready') || !this.hasAttribute('data-has-menu')) return;
    this.dialog = this.querySelector('dialog');
    this.opener = this.querySelector('[data-gw-open]');
    this.rail = this.querySelector('[data-gw-rail]');
    this.details = this.querySelector('[data-gw-details]');
    this.titleElement = this.querySelector('[data-gw-title]');
    this.back = this.querySelector('[data-gw-back]');
    this.controls = [...this.querySelectorAll('[data-gw-select]')];
    this.panels = [...this.querySelectorAll('[data-gw-panel]')];
    if (!this.dialog?.showModal || !this.opener || !this.rail || !this.details || !this.titleElement || !this.back) return;

    this.#abort = new AbortController();
    const options = { signal: this.#abort.signal };
    // Only the dialog moves. Section and block editor hosts remain in their original positions.
    this.dialog.removeAttribute('open');
    this.append(this.dialog);
    this.opener.hidden = false;
    this.querySelector('[data-gw-close]').hidden = false;
    this.controls.forEach((control) => {
      control.setAttribute('role', 'button');
      control.setAttribute('aria-controls', control.dataset.gwSelect);
      control.setAttribute('aria-expanded', 'false');
    });
    this.#layoutCards();
    this.#reset();
    this.addEventListener('click', this.#click, options);
    this.addEventListener('keydown', this.#key, options);
    this.dialog.addEventListener('cancel', this.#cancel, options);
    this.dialog.addEventListener('close', this.#closed, options);
    this.dialog.addEventListener('pointerdown', (event) => { this.#backdropDown = this.#outside(event); }, options);
    this.dialog.addEventListener('click', (event) => {
      if (this.#backdropDown && this.#outside(event)) this.close();
      this.#backdropDown = false;
    }, options);
    this.#media.addEventListener('change', this.#resize, options);
    // dialog:open does not bubble in Horizon; capture it at the document.
    document.addEventListener('click', this.#mobileClick, options);
    document.addEventListener('dialog:open', this.#otherOverlay, { ...options, capture: true });
    document.addEventListener('theme-drawer:open', this.#otherOverlay, options);
    document.addEventListener('shopify:block:select', this.#selectBlock, options);
    document.addEventListener('shopify:section:deselect', this.#deselectSection, options);
    window.addEventListener('pagehide', this.#pageHide, options);
    this.dataset.ready = '';
    this.#syncHeader();
    this.#observer = new MutationObserver(this.#syncHeader);
    const headerGroup = document.getElementById('header-group');
    if (headerGroup) this.#observer.observe(headerGroup, { childList: true, subtree: true });
    this.#visibilityObserver = new ResizeObserver(this.#syncHeader);
    this.#visibilityObserver.observe(this);
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.#visibilityObserver?.disconnect();
    if (!this.dialog) return;
    this.close(false);
    unlockScroll(this.dialog);
    delete this.dataset.ready;
    const header = document.getElementById('header-component');
    header?.removeAttribute('data-gw-navigation-active');
    header?.removeAttribute('data-gw-mobile-header-ready');
    header?.removeAttribute('data-gw-mobile-header-layout');
    if (this.mobileOpener) this.mobileOpener.hidden = true;
    updateAllHeaderCustomProperties();
    // Restore the fallback for a possible editor reinsert of this same element.
    this.querySelector('[data-gw-fallback]')?.append(this.dialog);
    this.dialog.setAttribute('open', '');
    this.opener.hidden = true;
    this.panels.forEach((panel) => { panel.hidden = false; });
    this.details.hidden = false;
    this.controls.forEach((control) => {
      control.removeAttribute('role');
      control.removeAttribute('aria-expanded');
      control.removeAttribute('aria-controls');
    });
  }

  #syncHeader = () => {
    const header = document.getElementById('header-component');
    const visible = this.getClientRects().length > 0;
    const active = this.isConnected && visible && this.dataset.replaceHeader === 'true' && this.hasAttribute('data-ready');
    if (!visible && this.dialog?.open) this.close(false);
    this.#syncMobileOpener(header, active);
    if (header?.hasAttribute('data-gw-navigation-active') === active) return;
    header?.toggleAttribute('data-gw-navigation-active', active);
    updateAllHeaderCustomProperties();
  };

  #syncMobileOpener(header, active) {
    this.mobileOpener = header?.querySelector('[data-gw-mobile-menu]');
    const ready = active && !!this.mobileOpener && header.hasAttribute('data-gw-compact-mobile');
    header?.toggleAttribute('data-gw-mobile-header-ready', ready);
    header?.toggleAttribute('data-gw-mobile-header-layout', ready);
    if (!this.mobileOpener) return;
    this.mobileOpener.hidden = !ready;
    this.mobileOpener.setAttribute('aria-controls', this.dialog.id);
    this.mobileOpener.setAttribute('aria-expanded', String(this.dialog.open));
  }
  #mobileClick = (event) => {
    const opener = event.target.closest('[data-gw-mobile-menu]');
    if (!opener || opener !== this.mobileOpener || opener.hidden) return;
    event.preventDefault(); this.open({ opener });
  };
  #setExpanded(expanded) {
    this.opener.setAttribute('aria-expanded', String(expanded));
    this.mobileOpener?.setAttribute('aria-expanded', String(expanded));
  }

  async open({ focus = true, opener = this.opener } = {}) {
    if (this.dialog.open || this.#opening) return;
    this.#opening = true;
    const request = ++this.#openRequest;
    // Settle existing overlays before making this dialog modal.
    const owners = [...document.querySelectorAll('dialog[open]')]
      .filter((dialog) => dialog !== this.dialog)
      .map((dialog) => dialog.closest('dialog-component, search-modal, quick-add-dialog, theme-drawer, gw-enquiry-widget'));
    await Promise.allSettled(owners.map(async (owner) => owner?.closeDialog?.() ?? owner?.close?.()));
    this.#opening = false;
    if (!this.isConnected || request !== this.#openRequest) return;
    this.#reset();
    this.returnFocus = opener;
    // Editor block refreshes must reveal artwork without taking focus from its settings.
    // Inert suppresses the native dialog autofocus step while it enters the top layer.
    this.dialog.inert = !focus;
    this.dialog.showModal();
    this.dialog.inert = false;
    lockScroll(this.dialog);
    this.#setExpanded(true);
    this.#materialize(this.rail);
    if (focus) (this.rail.querySelector('a') ?? this.querySelector('[data-gw-close]')).focus({ preventScroll: true });
  }

  close(restoreFocus = true) {
    this.#openRequest++;
    if (!this.dialog?.open) return;
    const otherFocus = !this.contains(document.activeElement) ? document.activeElement : null;
    this.dialog.close();
    unlockScroll(this.dialog);
    this.#reset();
    if (restoreFocus && this.isConnected) {
      const target = this.returnFocus?.isConnected && this.returnFocus.getClientRects().length
        ? this.returnFocus
        : this.opener.getClientRects().length ? this.opener : document.querySelector('header-component button');
      target?.focus({ preventScroll: true });
    } else if (otherFocus instanceof HTMLElement && otherFocus.isConnected) {
      otherFocus.focus({ preventScroll: true });
    }
  }

  #closed = () => {
    // Also covers external/native close calls without clobbering a rapid new opening.
    if (this.dialog.open) return;
    unlockScroll(this.dialog);
    this.#setExpanded(false);
  };
  #cancel = (event) => { event.preventDefault(); this.close(); };
  #pageHide = () => this.close(false);
  #otherOverlay = (event) => { if (!this.contains(event.target)) this.close(false); };
  #deselectSection = (event) => { if (event.target.contains(this)) this.close(false); };

  #reset() {
    this.#selected = null;
    this.#visits.clear();
    this.#railScroll = 0;
    this.rail.scrollTop = 0;
    this.details.scrollTop = 0;
    this.details.hidden = true;
    this.back.hidden = true;
    this.titleElement.textContent = this.titleElement.dataset.defaultTitle;
    this.dialog.removeAttribute('data-detail');
    this.#setExpanded(false);
    this.controls.forEach((control) => control.setAttribute('aria-expanded', 'false'));
    this.panels.forEach((panel) => { panel.hidden = true; });
  }

  #remember() {
    if (!this.#selected) return;
    const visit = this.#visits.get(this.#selected.id);
    visit.scroll = this.details.scrollTop;
    if (!this.#media.matches) visit.groups = [...this.#selected.querySelectorAll('[data-gw-group]')].map((group) => group.open);
  }

  select(id, { focus = true } = {}) {
    const panel = this.panels.find((item) => item.id === id);
    if (!panel) return;
    this.#remember();
    this.#railScroll = this.rail.scrollTop;
    this.#selected = panel;
    if (!this.#visits.has(id)) {
      this.#visits.set(id, { scroll: 0, groups: [...panel.querySelectorAll('[data-gw-group]')].map((_, index) => index === 0) });
    }
    this.panels.forEach((item) => { item.hidden = item !== panel; });
    this.controls.forEach((control) => control.setAttribute('aria-expanded', String(control.dataset.gwSelect === id)));
    this.dialog.setAttribute('data-detail', '');
    this.details.hidden = false;
    this.back.hidden = false;
    this.#applyGroups();
    this.titleElement.textContent = this.#media.matches ? this.titleElement.dataset.defaultTitle : panel.querySelector('h2').textContent;
    this.#materialize(panel);
    if (focus) this.#focusHeading();
    this.details.scrollTop = this.#visits.get(id).scroll;
  }

  #goBack() {
    const id = this.#selected?.id;
    this.#remember();
    this.#selected = null;
    this.dialog.removeAttribute('data-detail');
    this.details.hidden = true;
    this.back.hidden = true;
    this.panels.forEach((panel) => { panel.hidden = true; });
    this.titleElement.textContent = this.titleElement.dataset.defaultTitle;
    this.controls.forEach((control) => control.setAttribute('aria-expanded', 'false'));
    this.rail.scrollTop = this.#railScroll;
    this.controls.find((control) => control.dataset.gwSelect === id)?.focus({ preventScroll: true });
  }

  #applyGroups() {
    if (!this.#selected) return;
    const visit = this.#visits.get(this.#selected.id);
    this.#selected.querySelectorAll('[data-gw-group]').forEach((group, index) => {
      group.open = this.#media.matches || visit.groups[index];
      const summary = group.querySelector('summary');
      summary.tabIndex = this.#media.matches ? -1 : 0;
      if (this.#media.matches) summary.setAttribute('aria-disabled', 'true');
      else summary.removeAttribute('aria-disabled');
    });
  }

  #focusHeading() {
    const heading = this.#media.matches ? this.#selected?.querySelector('h2') : this.titleElement;
    heading?.focus({ preventScroll: true });
  }

  #layoutCards() {
    // Keep DOM, reading and tab order aligned with each layout; artwork has no editor wrappers.
    this.panels.forEach((panel) => {
      const cards = panel.querySelector('.gw-nav__cards');
      if (!cards) return;
      if (this.#media.matches) panel.insertBefore(cards, panel.querySelector('.gw-nav__groups'));
      else panel.append(cards);
    });
  }

  #resize = () => {
    this.#layoutCards();
    if (!this.#selected) return;
    // On entering desktop, the DOM still contains the previous mobile choices.
    if (this.#media.matches) this.#visits.get(this.#selected.id).groups = [...this.#selected.querySelectorAll('[data-gw-group]')].map((group) => group.open);
    this.#applyGroups();
    this.titleElement.textContent = this.#media.matches ? this.titleElement.dataset.defaultTitle : this.#selected.querySelector('h2').textContent;
    if (this.dialog.open && !document.activeElement?.getClientRects().length) this.#focusHeading();
  };

  #selectBlock = async (event) => {
    const host = event.target.closest('[data-gw-block]');
    if (!host || !this.contains(host) || !host.dataset.gwTarget) return;
    await this.open({ focus: false });
    this.select(host.dataset.gwTarget, { focus: false });
  };

  #click = (event) => {
    const target = event.target.closest('button, a, summary');
    if (!target || !this.contains(target)) return;
    if (target.matches('[data-gw-open]')) this.open();
    else if (target.matches('[data-gw-close]')) this.close();
    else if (target.matches('[data-gw-back]')) this.#goBack();
    else if (target.matches('[data-gw-select]')) { event.preventDefault(); this.select(target.dataset.gwSelect); }
    else if (this.#media.matches && target.matches('[data-gw-group] > summary')) event.preventDefault();
  };
  #key = (event) => {
    if (event.key === 'Escape' && this.dialog.open) { event.preventDefault(); event.stopPropagation(); this.close(); }
    if (event.key === ' ' && event.target.matches('[data-gw-select]')) { event.preventDefault(); this.select(event.target.dataset.gwSelect); }
    if (event.key === 'Tab' && this.dialog.open) {
      const controls = [...this.dialog.querySelectorAll('a[href], button, summary, [tabindex="0"]')]
        .filter((control) => control.tabIndex >= 0 && !control.disabled && control.getClientRects().length);
      const first = controls[0];
      const last = controls.at(-1);
      if (event.shiftKey && event.target === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && event.target === last) { event.preventDefault(); first?.focus(); }
    }
  };
  #outside(event) {
    if (event.target !== this.dialog) return false;
    const rect = this.dialog.getBoundingClientRect();
    return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
  }
  #materialize(root) {
    root.querySelectorAll('[data-gw-image] > template').forEach((template) => {
      template.replaceWith(template.content.cloneNode(true));
    });
  }
}

if (!customElements.get('gw-category-navigation')) customElements.define('gw-category-navigation', GWCategoryNavigation);
